import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, ShoppingBag, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtTRY } from "../../lib/format";
import { useStore } from "../../context/StoreContext";

const SID_KEY = "bigeldi_chat_sid";

function categorySlugFromPath(pathname) {
  if (pathname.startsWith("/dadya-parfum")) return "dadya-parfum";
  if (pathname.startsWith("/kategori/")) return pathname.split("/")[2] || null;
  return null;
}

function ChatProductCard({ p }) {
  const { addToCart } = useStore();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (p.has_variants) {
      navigate(`/urun/${p.slug}`);
      return;
    }
    setAdding(true);
    await addToCart(p.product_id, null, 1);
    setAdding(false);
  };

  return (
    <div className="flex gap-3 bg-white border border-[#E6E4DD] rounded-xl p-2.5 items-center"
      data-testid={`chat-product-card-${p.product_id}`}>
      <Link to={`/urun/${p.slug}`} className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-[#F4F2EB] block"
        data-testid={`chat-product-link-${p.product_id}`}>
        {p.image && <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover" />}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/urun/${p.slug}`} className="text-[12px] font-semibold text-[#121214] leading-snug line-clamp-2 hover:text-amber-700">
          {p.name}
        </Link>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-[13px] font-bold text-[#121214]" data-testid={`chat-product-price-${p.product_id}`}>{fmtTRY(p.price)}</span>
          {p.list_price > p.price && <span className="text-[11px] text-neutral-400 line-through">{fmtTRY(p.list_price)}</span>}
          {p.campaign_name && <span className="text-[10px] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">{p.campaign_name}</span>}
        </div>
      </div>
      {p.requires_quote ? (
        <Link to={`/urun/${p.slug}`}
          className="shrink-0 h-8 px-3 rounded-full bg-[#121214] text-white text-[11px] font-semibold flex items-center hover:bg-amber-600 transition-colors"
          data-testid={`chat-product-quote-${p.product_id}`}>Teklif Al</Link>
      ) : (
        <button onClick={handleAdd} disabled={adding}
          className="shrink-0 h-8 px-3 rounded-full bg-[#121214] text-white text-[11px] font-semibold flex items-center gap-1.5 hover:bg-amber-600 transition-colors disabled:opacity-50"
          data-testid={`chat-product-add-${p.product_id}`}>
          <ShoppingBag size={12} /> {p.has_variants ? "İncele" : adding ? "..." : "Sepete Ekle"}
        </button>
      )}
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const location = useLocation();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const ctx = useCallback(() => ({
    path: location.pathname,
    category_slug: categorySlugFromPath(location.pathname),
  }), [location.pathname]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const startChat = useCallback(async () => {
    if (started) return;
    setStarted(true);
    const sid = sessionStorage.getItem(SID_KEY);
    try {
      if (sid) {
        const { data } = await api.get(`/chat/history/${sid}`);
        if (data.messages?.length) {
          setMessages(data.messages);
          return;
        }
      }
      setBusy(true);
      const { data } = await api.post("/chat/message", { session_id: sid, action: "start", ...ctx() });
      if (data.session_id) sessionStorage.setItem(SID_KEY, data.session_id);
      if (data.message) setMessages([data.message]);
    } catch {
      setMessages([{ role: "assistant", text: "Merhaba! Şu an bağlantıda sorun yaşıyorum, lütfen birazdan tekrar deneyin." }]);
    } finally {
      setBusy(false);
    }
  }, [started, ctx]);

  useEffect(() => {
    if (open) startChat();
  }, [open, startChat]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: t }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/message", {
        session_id: sessionStorage.getItem(SID_KEY), action: "message", text: t, ...ctx(),
      });
      if (data.session_id) sessionStorage.setItem(SID_KEY, data.session_id);
      if (data.message) setMessages((m) => [...m, data.message]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Bağlantı hatası oluştu. Lütfen tekrar deneyin." }]);
      toast.error("Mesaj gönderilemedi");
    } finally {
      setBusy(false);
    }
  };

  const resetChat = async () => {
    sessionStorage.removeItem(SID_KEY);
    setMessages([]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/message", { session_id: null, action: "start", ...ctx() });
      if (data.session_id) sessionStorage.setItem(SID_KEY, data.session_id);
      if (data.message) setMessages([data.message]);
    } catch { /* sessiz */ } finally { setBusy(false); }
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const quickReplies = !busy ? lastAssistant?.quick_replies : null;

  return (
    <>
      {/* Floating buton */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-[70] h-14 w-14 rounded-full bg-[#121214] text-white shadow-[0_12px_35px_-8px_rgba(18,18,20,0.5)] flex items-center justify-center hover:bg-amber-600 hover:scale-105 active:scale-95 transition-all duration-300 group"
        data-testid="chat-widget-button" aria-label="Sohbet asistanını aç">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-amber-500 text-[10px] font-bold flex items-center justify-center animate-pulse-soft">
            <Sparkles size={11} />
          </span>
        )}
      </button>

      {/* Sohbet paneli — mobilde tam ekran */}
      {open && (
        <div
          className="fixed z-[70] bg-[#FAF9F5] border border-[#E6E4DD] shadow-[0_25px_80px_-20px_rgba(18,18,20,0.45)] flex flex-col overflow-hidden
            inset-0 sm:inset-auto sm:bottom-24 sm:right-5 sm:w-[390px] sm:h-[600px] sm:max-h-[calc(100vh-120px)] sm:rounded-3xl"
          data-testid="chat-panel">
          {/* Başlık */}
          <div className="bg-[#121214] text-white px-4 py-3.5 flex items-center gap-3 shrink-0">
            <img src="/assets/bigeldi-logo.png" alt="Bigeldi" className="h-8 w-8 rounded-full object-contain bg-white p-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm leading-tight">Bigeldi Asistan</p>
              <p className="text-[11px] text-neutral-400 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block" /> Çevrimiçi — size yardımcı olmaya hazırım
              </p>
            </div>
            <button onClick={resetChat} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
              data-testid="chat-reset-button" aria-label="Sohbeti sıfırla">
              <RotateCcw size={15} />
            </button>
            <button onClick={() => setOpen(false)} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center text-neutral-400 hover:text-white transition-colors sm:hidden"
              data-testid="chat-close-button" aria-label="Kapat">
              <X size={17} />
            </button>
          </div>

          {/* Mesajlar */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 py-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full max-w-[92%]"}`}>
                  <div
                    className={`px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-line ${
                      m.role === "user"
                        ? "bg-[#121214] text-white rounded-2xl rounded-br-md"
                        : "bg-white border border-[#E6E4DD] text-[#121214] rounded-2xl rounded-bl-md"
                    }`}
                    data-testid={`chat-message-${m.role}-${i}`}>
                    {m.text}
                  </div>
                  {m.products?.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {m.products.map((p) => <ChatProductCard key={p.product_id} p={p} />)}
                    </div>
                  )}
                  {m.links?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {m.links.map((l, j) => (
                        <Link key={j} to={l.url} onClick={() => { if (window.innerWidth < 640) setOpen(false); }}
                          className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full hover:bg-amber-100 transition-colors"
                          data-testid={`chat-link-${i}-${j}`}>{l.label} →</Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="chat-typing-indicator">
                <div className="bg-white border border-[#E6E4DD] rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Hızlı cevaplar */}
          {quickReplies?.length > 0 && (
            <div className="px-3.5 pb-2 flex flex-wrap gap-1.5 shrink-0" data-testid="chat-quick-replies">
              {quickReplies.map((q, qi) => (
                <button key={q} onClick={() => send(q)}
                  className="text-[12px] font-medium bg-white border border-[#D6D3C8] text-[#121214] px-3 py-1.5 rounded-full hover:border-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors"
                  data-testid={`chat-quick-reply-${qi}`}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Giriş */}
          <div className="p-3 border-t border-[#E6E4DD] bg-white shrink-0">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Mesajınızı yazın..."
                maxLength={1000}
                className="flex-1 h-11 px-4 bg-[#F4F2EB] rounded-full text-[13px] text-[#121214] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                data-testid="chat-input"
              />
              <button type="submit" disabled={busy || !input.trim()}
                className="h-11 w-11 rounded-full bg-amber-600 text-white flex items-center justify-center hover:bg-amber-700 active:scale-95 transition-all disabled:opacity-40 shrink-0"
                data-testid="chat-send-button" aria-label="Gönder">
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
